<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import type { Ref } from 'vue';
import type { LngLatBounds, Map as MapboxMap } from 'mapbox-gl';

import {
  useBrowserStore,
  useEditingIngredientStore,
  useMapStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import {
  ProductSelectionMapLayout,
  Mapbox,
} from '@apps/main/new-desktop/modules/newMaps';
import { useFiltersStore } from '@apps/main/new-desktop/core/stores/filters/filtersStore';
import { useHotelFiltersStore } from '@apps/main/new-desktop/modules/hotelFilters/store/hotelFiltersStore';

import { AppFlicker } from '@libs/ui';
import type { IngredientId } from '@libs/trip-planner-interfaces';
import type { Rectangle } from '@libs/maps';

import {
  useHotelSelectionMapStore,
  useHotelSelectionModalsStore,
  useHotelSelectionStore,
} from '../stores';
import { HotelSelectionModalType } from '../models';

import HotelMapCard from './HotelMapCard.vue';

const browserStore = useBrowserStore();
const hotelSelectionStore = useHotelSelectionStore();
const hotelSelectionMapStore = useHotelSelectionMapStore();
const statsigEventsStore = useStatsigEventsStore();
const hotelSelectionModalsStore = useHotelSelectionModalsStore();
const mapStore = useMapStore();
const hotelFiltersStore = useHotelFiltersStore();
const filtersStore = useFiltersStore();
const editingIngredientStore = useEditingIngredientStore();

// State
const shouldShowSearchThisAreaButton: Ref<boolean> = ref(false);
const initialLoadDone = ref<boolean>(false);

const showMapbox = computed<boolean>(
  () => initialLoadDone.value || !hotelSelectionStore.shouldDisplayLoader
);

function onExposeMapInstance(map: MapboxMap): void {
  hotelSelectionMapStore.mapRef = map;
  initialLoadDone.value = true;
}

function showSearchThisAreaButton(): void {
  shouldShowSearchThisAreaButton.value = true;
}

function boundsToRectangle(bounds: LngLatBounds): Rectangle {
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();

  return {
    topLeft: {
      latitude: ne.lat,
      longitude: sw.lng,
    },
    bottomRight: {
      latitude: sw.lat,
      longitude: ne.lng,
    },
  };
}

function onSearchThisArea(): void {
  shouldShowSearchThisAreaButton.value = false;
  hotelSelectionMapStore.savedBounds = getCurrentBounds();

  if (hotelSelectionMapStore.savedBounds) {
    const event = {
      newView: boundsToRectangle(hotelSelectionMapStore.savedBounds),
      userInitiated: true,
    };

    if (editingIngredientStore.selectedNeigborhoodFilters.length) {
      clearNeighborhoodPolygonsFiltersSelection();
      setTimeout(() => {
        mapStore.setMapMovedAndCreateNewPolygon(event);
        // if (hotelSelectionMapStore.savedBounds) {
        //   drawBoundsRectangle(hotelSelectionMapStore.savedBounds);
        // }
      }, 0);
    } else {
      mapStore.setMapMovedAndCreateNewPolygon(event);
      // drawBoundsRectangle(hotelSelectionMapStore.savedBounds);
    }

    statsigEventsStore.logHotelSelectionSearchThisAreaClicked();
  }
}

function clearNeighborhoodPolygonsFiltersSelection(): void {
  hotelFiltersStore.clearNeighborhoodPolygonsFilters();
  filtersStore.clearNeighborhoodPolygonsFilters();
}

function getCurrentBounds(): LngLatBounds | undefined {
  if (!hotelSelectionMapStore.mapRef) {
    return undefined;
  }
  return hotelSelectionMapStore.mapRef.getBounds() ?? undefined;
}

let removeBoundsButtonEl: HTMLButtonElement | null = null;

// function addRemoveBoundsButton(
//   map: MapboxMap,
//   coordinates: number[][][]
// ): void {
//   if (!coordinates?.[0]?.length) {
//     return;
//   }
//   const polygon = coordinates[0];
//
//   // compute bounding box
//   const lngs = polygon.map(([lng]) => lng ?? 0);
//   const lats = polygon.map(([, lat]) => lat ?? 0);
//   const minLng = Math.min(...lngs);
//   const maxLat = Math.max(...lats);
//   const topRight: [number, number] = [minLng, maxLat];
//
//   // create / reuse DOM button
//   if (!removeBoundsButtonEl) {
//     removeBoundsButtonEl = document.createElement('button');
//     removeBoundsButtonEl.type = 'button';
//     removeBoundsButtonEl.textContent = '✕';
//     Object.assign(removeBoundsButtonEl.style, {
//       position: 'absolute',
//       background: '#fff',
//       border: '1px solid #e4e4e4',
//       borderRadius: '50%',
//       cursor: 'pointer',
//       width: '30px',
//       height: '30px',
//       fontSize: '18px',
//       lineHeight: '26px',
//       textAlign: 'center',
//       boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
//       zIndex: '10',
//     } as Partial<CSSStyleDeclaration>);
//     map.getContainer().appendChild(removeBoundsButtonEl);
//
//     removeBoundsButtonEl.addEventListener('click', () => {
//       statsigEventsStore.logHotelSelectionRemovePolygonBounds();
//       if (map.getLayer('bounds-layer')) {
//         map.removeLayer('bounds-layer');
//       }
//       if (map.getSource('bounds-source')) {
//         map.removeSource('bounds-source');
//       }
//       removeBoundsButtonEl?.remove();
//       removeBoundsButtonEl = null;
//     });
//   }
//
//   // function to sync button position
//   const updatePosition = (): void => {
//     if (!removeBoundsButtonEl) {
//       return;
//     }
//     const pixel = map.project(topRight);
//     const offset = 15;
//     removeBoundsButtonEl.style.left = `${pixel.x - offset}px`;
//     removeBoundsButtonEl.style.top = `${pixel.y - offset}px`;
//   };
//
//   updatePosition();
//
//   // re-position when map moves or resizes
//   const reposition = (): void => updatePosition();
//   map.on('move', reposition);
//   map.on('zoom', reposition);
//   map.on('resize', reposition);
// }

// function drawBoundsRectangle(bounds: LngLatBounds): void {
//   if (!hotelSelectionMapStore.mapRef) {
//     return;
//   }
//
//   const sw = bounds.getSouthWest();
//   const ne = bounds.getNorthEast();
//
//   const coords: number[][][] = [
//     [
//       [sw.lng, sw.lat],
//       [ne.lng, sw.lat],
//       [ne.lng, ne.lat],
//       [sw.lng, ne.lat],
//       [sw.lng, sw.lat],
//     ],
//   ];
//
//   if (hotelSelectionMapStore.mapRef.getLayer('bounds-layer')) {
//     hotelSelectionMapStore.mapRef.removeLayer('bounds-layer');
//   }
//   if (hotelSelectionMapStore.mapRef.getSource('bounds-source')) {
//     hotelSelectionMapStore.mapRef.removeSource('bounds-source');
//   }
//
//   hotelSelectionMapStore.mapRef.addSource('bounds-source', {
//     type: 'geojson',
//     data: {
//       type: 'Feature',
//       geometry: { type: 'Polygon', coordinates: coords },
//       properties: {},
//     },
//   });
//
//   hotelSelectionMapStore.mapRef.addLayer(
//     {
//       id: 'bounds-layer',
//       type: 'fill',
//       source: 'bounds-source',
//       paint: {
//         'fill-color': '#8b53ef',
//         'fill-opacity': 0.2,
//         'fill-outline-color': '#8b53ef',
//       },
//     },
//     'clusters'
//   );
//
//   addRemoveBoundsButton(hotelSelectionMapStore.mapRef, coords);
// }

async function onToggleFullScreen(): Promise<void> {
  if (!hotelSelectionMapStore.mapRef) {
    // eslint-disable-next-line no-console
    console.log(
      'HotelSelectionMap.vue > onToggleFullScreen > map not defiend!'
    );
  }

  if (hotelSelectionMapStore.isFullScreen) {
    statsigEventsStore.logHotelSelectionCollapseMap();
  } else {
    statsigEventsStore.logHotelSelectionExtendMap();
  }

  hotelSelectionMapStore.onToggleFullScreen(
    !hotelSelectionMapStore.isFullScreen
  );
  await nextTick();
  hotelSelectionMapStore.mapRef?.resize();
}

async function onHotelCardClicked(): Promise<void> {
  statsigEventsStore.logHotelSelectionMapCardClicked({
    mapSize: hotelSelectionMapStore.hotelMapCardType,
    hotelCode: hotelSelectionMapStore.highlightedHotel?.hotelCode || '',
  });
  await hotelSelectionMapStore.hotelCardClicked();
}

function onPeekPrice(): void {
  if (!hotelSelectionMapStore.hotelMapCardData) {
    return;
  }
  statsigEventsStore.logHotelSelectionPeekPriceModalOpened({
    source: 'mapMarker',
  });
  hotelSelectionModalsStore.openPeekPriceModal(
    hotelSelectionMapStore.hotelMapCardData.ingredientIndex,
    hotelSelectionMapStore.hotelMapCardData?.hotelCode
  );
}

function onHide(): void {
  if (
    hotelSelectionModalsStore.hotelSelectionModalType ===
    HotelSelectionModalType.PeekPrice
  ) {
    return;
  }
  hotelSelectionMapStore.hideHotelCard();
  hotelSelectionMapStore.resetInspect();
}

function onMarkerClick(data: {
  hotelCode: string;
  ingredientId: IngredientId;
}): void {
  hotelSelectionMapStore.showHotelMapCard(data.ingredientId, data.hotelCode);
  hotelSelectionMapStore.inspect(data.ingredientId, data.hotelCode);
}

function onClusterClick(): void {
  statsigEventsStore.logHotelSelectionMapClusterClicked();
}

watch(
  () => hotelSelectionMapStore.savedBounds,
  (bounds) => {
    if (!hotelSelectionMapStore.mapRef) {
      return;
    }

    if (bounds === undefined) {
      if (hotelSelectionMapStore.mapRef.getLayer('bounds-layer')) {
        hotelSelectionMapStore.mapRef.removeLayer('bounds-layer');
      }
      if (hotelSelectionMapStore.mapRef.getSource('bounds-source')) {
        hotelSelectionMapStore.mapRef.removeSource('bounds-source');
      }
      removeBoundsButtonEl?.remove();
      removeBoundsButtonEl = null;
    }
  },
  { deep: true }
);

onUnmounted(() => {
  hotelSelectionMapStore.mapRef = undefined;
});
</script>

<template>
  <ProductSelectionMapLayout
    :map-layout="browserStore.isLargerThan1199px ? 'desktop' : 'mobile'"
    :is-desktop-map-full-screen="hotelSelectionMapStore.isFullScreen"
    :should-show-search-this-area-button="shouldShowSearchThisAreaButton"
    :active-filters-count="hotelSelectionStore.activeFiltersCount"
    :should-display-loader="hotelSelectionStore.shouldDisplayLoader"
    @toggle-full-screen="onToggleFullScreen"
    @search-this-area="onSearchThisArea"
    @mouse-leave="onHide"
  >
    <template #map>
      <AppFlicker
        v-if="!initialLoadDone"
        width="100%"
        height="100%"
        border-radius="0"
      />

      <Mapbox
        v-if="showMapbox"
        :data="hotelSelectionMapStore.mapboxProperties"
        :should-show-zoom-controls="browserStore.isLargerThan1199px"
        @expose-instance="onExposeMapInstance"
        @dragend="showSearchThisAreaButton"
        @zoomend="showSearchThisAreaButton"
        @marker-click="onMarkerClick($event)"
        @cluster-click="onClusterClick()"
      />
    </template>

    <template #product-card>
      <HotelMapCard
        v-if="
          hotelSelectionMapStore.highlightedHotel?.showHotelMapCard &&
          hotelSelectionMapStore.hotelMapCardData
        "
        :data="hotelSelectionMapStore.hotelMapCardData"
        :card-type="hotelSelectionMapStore.hotelMapCardType"
        class="hotel-map-card"
        @click="onHotelCardClicked"
        @open-peek-price-calendar="onPeekPrice"
        @hide="onHide"
      />
    </template>
  </ProductSelectionMapLayout>
</template>
